import type { MouseEvent } from 'react';
import { ArrowUpRight, Calendar, MessageCircle } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useAgentName } from '../../../lib/use-agent-name';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useItemSeenBi } from '../../../lib/use-item-seen-bi';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { avatarSlotFor } from '../../../lib/avatar-color';
import { connectorLogoUrl } from '../../../lib/connectors';
import { GOAL_INTENTS, goalIntentContext, type GoalConnector, type GoalIntent } from '../../../lib/goal-intents';
import { Avatar, Badge, Card, TextButton, Typography } from '../ui';
import styles from './GoalIntentGrid.module.css';

const PAGE = 'dashboard';

/**
 * The empty home: "What's your goal?" — five goal cards and a
 * "Just chat" card. Picking a goal opens the side chat on a seed that asks the Maestro to build the band
 * the card names, with the hidden brief (`goalIntentContext`) riding as the turn's `context`, the same
 * channel the "+ New goal" seed uses. The Maestro creates the band on that turn.
 *
 * The whole tile is the hit target AND keeps its CTA — the Cards rule's nested-control exception: a `div`
 * card with the full `interactive` state and an `onClick`, no role or tab stop of its own (the CTA is the
 * keyboard/screen-reader path to the same action), and clicks that landed on the CTA ignored so the chat
 * is not opened twice. The catalog is static today — `lib/goal-intents.ts` says where its entries come from.
 */
/** `page`: the BI page the grid reports its clicks under, since the Wins page's empty state renders it too. */
export function GoalIntentGrid({ page = PAGE }: { page?: string } = {}) {
  const { t } = useTranslation();
  const agent = useAgentName();
  const { logPageClickBi } = useGenericBi();
  const openSideChat = useOpenSideChat();
  const seenRef = useItemSeenBi('goal-intent-grid');

  const build = (goal: GoalIntent) => {
    logPageClickBi({ page_name: page, button_name: 'build_band' }, { componentName: `build-band-${goal.id}` });
    openSideChat({ kind: 'new', message: t(goal.seedKey), context: goalIntentContext(goal) });
  };

  const justChat = () => {
    logPageClickBi({ page_name: page, button_name: 'just_chat' });
    openSideChat({ kind: 'new', message: t('home.goals.chat.seed') });
  };

  // A brand name is the same in every language; the two glyph chips are translated.
  const connectorLabel = (connector: GoalConnector): string =>
    connector.kind === 'toolkit'
      ? connector.name
      : t(connector.kind === 'calendar' ? 'home.goals.connector.calendar' : 'home.goals.connector.messages');

  const fromCardBackground = (run: () => void) => (e: MouseEvent<HTMLElement>) => {
    if ((e.target as HTMLElement).closest('a,button')) return;
    run();
  };

  return (
    <div className={styles.root}>
      {/* The impression is measured on the HEADER, not the whole grid: the observer fires at 50% visible,
          and on a phone the stacked grid is taller than the viewport and would never get there. */}
      <header ref={seenRef}>
        <Typography variant="h1" component="h1" color="strong" className={styles.title}>
          {t('home.goals.title')}
        </Typography>
        <Typography variant="p2" color="secondary" className={styles.subtitle}>
          {t('home.goals.subtitle')}
        </Typography>
      </header>
      <div className={styles.grid}>
        {GOAL_INTENTS.map((goal) => (
          <Card
            key={goal.id}
            variant="sunken"
            size="lg"
            interactive
            className={styles.card}
            data-goal={goal.id}
            onClick={fromCardBackground(() => build(goal))}
          >
            <div className={styles.cardHead}>
              <Typography variant="h4" component="h3" color="strong" className={styles.cardTitle}>
                {t(goal.titleKey)}
              </Typography>
              <Badge variant="agents" size="tiny" label={t(goal.badgeKey)} className={styles.cardLabel} />
            </div>
            <Typography variant="p3" color="secondary" className={styles.cardOneliner}>
              {t(goal.onelinerKey)}
            </Typography>
            <div className={styles.teamRow}>
              <span className={styles.teamAvatars}>
                {goal.seats.map((seat) => (
                  <Avatar key={seat.name} initials={seat.name.charAt(0)} size={24} color={avatarSlotFor(seat.name)} className={styles.teamAvatar} />
                ))}
              </span>
              <Typography variant="p3" component="span" color="secondary">
                {t(goal.bandNameKey)}
              </Typography>
            </div>
            <div className={styles.connBlock}>
              {goal.connectors.length ? (
                <>
                  <Typography variant="p3" component="span" color="secondary" className={styles.connLabel}>
                    {t('home.goals.connectsTo')}
                  </Typography>
                  <div className={styles.connRow}>
                    {goal.connectors.map((connector, i) => (
                      <ConnectorChip key={i} connector={connector} label={connectorLabel(connector)} />
                    ))}
                  </div>
                </>
              ) : (
                <Typography variant="p3" component="span" color="secondary" className={styles.connNone}>
                  {t('home.goals.noConnectors')}
                </Typography>
              )}
            </div>
            <TextButton size="small" endIcon={<ArrowUpRight size={16} className="dir-flip" aria-hidden />} className={styles.cardCta} onClick={() => build(goal)}>
              {t('home.goals.cta')}
            </TextButton>
          </Card>
        ))}
        <Card variant="outline" size="lg" interactive className={styles.cardChat} onClick={fromCardBackground(justChat)}>
          <Typography variant="p2" color="secondary" align="center">
            {t('home.goals.chat.title', { maestroAgentName: agent.short })}
          </Typography>
          <TextButton size="small" endIcon={<ArrowUpRight size={16} className="dir-flip" aria-hidden />} onClick={justChat}>
            {t('home.goals.chat.cta')}
          </TextButton>
        </Card>
      </div>
    </div>
  );
}

/**
 * One "Connects to" chip. An app draws its Composio logo; the calendar and messages are glyphs, because
 * neither is an app the band connects (the calendar has its own link, messages are a channel). The chip
 * is an image NAMED by `label`, so a screen reader hears "Gmail" after "Connects to"; the mark inside
 * stays decorative.
 */
function ConnectorChip({ connector, label }: { connector: GoalConnector; label: string }) {
  return (
    <span className={styles.connIcon} role="img" aria-label={label}>
      {connector.kind === 'toolkit' ? (
        <img src={connectorLogoUrl(connector.toolkit)} alt="" width={16} height={16} aria-hidden />
      ) : connector.kind === 'calendar' ? (
        <Calendar size={14} aria-hidden />
      ) : (
        <MessageCircle size={14} aria-hidden />
      )}
    </span>
  );
}
